import { formatEur, type AboInvoiceDocDTO } from '@wf/shared'
import { formatDateDE } from '../time/format'

/**
 * Beleg-Körper einer Abo-Rechnung (Cevio → Werkstatt). Nutzt bewusst dieselben `wf-invoice`-CSS-Klassen wie die
 * §14-Kundenrechnung (einheitliches Druck-/PDF-Layout), rendert aber die abo-spezifischen Pflichtinhalte:
 * Rechnungssteller = Cevio, Empfänger = Werkstatt-Firmendaten, Leistungszeitraum (§14 Abs. 4 Nr. 6), eine
 * Positionszeile (Tarif-Beitrag der Periode) + Netto/USt/Brutto und die SEPA-Lastschrift-Vorabankündigung.
 * Alle Werte kommen fertig eingefroren aus dem Beleg-DTO; Geld nur über `formatEur`.
 */
export function AboInvoiceDocument({ invoice }: { invoice: AboInvoiceDocDTO }) {
  const s = invoice.seller
  const b = invoice.buyer
  const addonNet = invoice.addonNet ?? 0 // defensiv (Altbelege ohne Feld → 0)
  const baseNet = invoice.netTotal - addonNet // Netto-Anteil der Tarif-Grundzeile (ohne Upgrade-Zuschlag)
  const recipientName = b.legalName || b.name
  const sellerCity = [s.zip, s.city].filter(Boolean).join(' ')
  const buyerCity = [b.zip, b.city].filter(Boolean).join(' ')
  return (
    <article className="wf-invoice">
      <header className="wf-invoice__head">
        <div className="wf-invoice__biz">
          <div className="wf-invoice__bizname">{s.name}</div>
          {s.legalName && s.legalName !== s.name ? <div className="wf-invoice__bizline">{s.legalName}</div> : null}
          <div className="wf-invoice__bizline">
            {s.address}
            {sellerCity ? `, ${sellerCity}` : ''}
            {s.country ? ` · ${s.country}` : ''}
          </div>
          {s.email ? <div className="wf-invoice__bizline">{s.email}</div> : null}
          {s.taxNumber || s.vatId ? (
            <div className="wf-invoice__bizline">
              {s.taxNumber ? `St.-Nr. ${s.taxNumber}` : ''}
              {s.taxNumber && s.vatId ? ' · ' : ''}
              {s.vatId ? `USt-IdNr. ${s.vatId}` : ''}
            </div>
          ) : null}
        </div>
        <div className="wf-invoice__title">
          <div className="wf-invoice__doc">Rechnung</div>
          <div className="wf-invoice__no wf-tnum">{invoice.invoiceNumber}</div>
          <div className="wf-invoice__date">{formatDateDE(invoice.issueDate)}</div>
        </div>
      </header>

      <div className="wf-invoice__to">
        <div className="wf-invoice__tolabel">Rechnungsempfänger</div>
        <div className="wf-invoice__toname">{recipientName}</div>
        {b.address ? <div>{b.address}</div> : null}
        {buyerCity ? <div>{buyerCity}</div> : null}
        {b.vatId ? <div className="muted">USt-IdNr. {b.vatId}</div> : null}
      </div>

      <div className="wf-invoice__work">
        Leistungszeitraum: {formatDateDE(invoice.periodStart)} – {formatDateDE(invoice.periodEnd)}
      </div>

      <table className="wf-invoice__lines">
        <thead>
          <tr>
            <th>Bezeichnung</th>
            <th className="wf-invoice__num">Menge</th>
            <th className="wf-invoice__num">Nettopreis</th>
            <th className="wf-invoice__num">Steuer</th>
            <th className="wf-invoice__num">Gesamt netto</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            {/* Tarif-Modell: eine Grundzeile für den Tarif-Beitrag der Periode (Menge 1). Der Leistungszeitraum oben
                gibt Monat/Jahr an; ein etwaiger untermonatlicher Upgrade-Zuschlag steht als eigene Zeile darunter. */}
            <td>Cevio – Werkstatt-Software · Abo-Beitrag (Leistungszeitraum)</td>
            <td className="wf-invoice__num">1</td>
            <td className="wf-invoice__num wf-tnum">{formatEur(baseNet)}</td>
            <td className="wf-invoice__num">{invoice.vatRate} %</td>
            <td className="wf-invoice__num wf-tnum">{formatEur(baseNet)}</td>
          </tr>
          {addonNet > 0 && (
            <tr>
              <td>Anteiliger Tarif-Upgrade (untermonatlich)</td>
              <td className="wf-invoice__num">—</td>
              <td className="wf-invoice__num wf-tnum">—</td>
              <td className="wf-invoice__num">{invoice.vatRate} %</td>
              <td className="wf-invoice__num wf-tnum">{formatEur(addonNet)}</td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="wf-invoice__totals">
        <div className="wf-invoice__total">
          <span>Gesamt netto</span>
          <span className="wf-tnum">{formatEur(invoice.netTotal)}</span>
        </div>
        <div className="wf-invoice__total">
          <span>zzgl. {invoice.vatRate} % USt</span>
          <span className="wf-tnum">{formatEur(invoice.taxTotal)}</span>
        </div>
        <div className="wf-invoice__total wf-invoice__total--gross">
          <span>Bruttogesamtbetrag</span>
          <span className="wf-tnum">{formatEur(invoice.grossTotal)}</span>
        </div>
      </div>

      <p className="wf-invoice__pay">
        Der Rechnungsbetrag von {formatEur(invoice.grossTotal)} wird per SEPA-Lastschrift eingezogen — bitte für
        Kontodeckung sorgen.
        {invoice.sepa.creditorId ? ` Gläubiger-ID: ${invoice.sepa.creditorId}.` : ''}
        {invoice.sepa.mandateReference ? ` Mandatsreferenz: ${invoice.sepa.mandateReference}.` : ''}
        {invoice.sepa.collectionDate ? ` Voraussichtliches Einzugsdatum: ${formatDateDE(invoice.sepa.collectionDate)}.` : ''}
      </p>
    </article>
  )
}
